<script>
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import ActionFooter from '@components/footer/action-footer.vue';
import draggable from 'vuedraggable';
import HeroExercise from '@components/hero/hero-exercise.vue';
import moment from 'moment';
import PhotoUploader from '@components/uploaders/photo-uploader.vue';
import SectionWrapper from '@components/section/section-wrapper.vue';
import StepListItem from '@components/step/step-list-item.vue';
import VideoUploader from '@components/uploaders/video-uploader.vue';
import { adminPanelMixin } from '@mixins/admin-panel-mixin.js';
import { helperMixin } from '@mixins/helper-mixin';
import { cleverTapMixin } from '@mixins/cleverTap-mixin';
import {
  adminPanelComputed,
  adminPanelMethods,
  authComputed,
  equipmentComputed,
  equipmentMethods,
  exerciseComputed,
  exerciseMethods,
  exerciseDifficultyComputed,
  exerciseDifficultyMethods,
  mediaComputed,
  mediaMethods,
  muscleGroupComputed,
  muscleGroupMethods,
  modalMethods,
  workoutMethods,
  circuitMethods,
} from '@state/helpers';

export default {
  components: {
    ActionFooter,
    draggable,
    HeroExercise,
    PhotoUploader,
    SectionWrapper,
    StepListItem,
    VideoUploader,
  },
  props: {
    exercise: {
      type: Object,
      required: true,
      default: () => {},
    },
    inPanel: {
      type: Boolean,
      required: false,
    },
  },
  mixins: [adminPanelMixin, helperMixin, cleverTapMixin],
  data() {
    return {
      drag: false,
      exerciseClone: null,
      footerWidth: '',
      newDifficulty: null,
      newMuscleGroups: null,
      newWeightedEquipment: null,
      newUnweightedEquipment: [],
      newTips: [],
      steps: [],
      updatedExerciseClone: {},
      mediaKey: 0,
      exerciseObj: {},
      initExercise: {},
      hasTipsChanges: false,
      hasStepsChanges: false,
      hasExerciseChanges: false,
      hasWeightedEquipmentChanges: false,
      hasAltExerciseChanges: false,
      isSaving: false,
      lastSaved: null,
      tempVideoUrl: null,
      newCoverPhotoUrl: null,
      newThumbnailPhotoUrl: null,
      newVideoUrl: null,
      newVideoType: null,
    };
  },
  computed: {
    ...adminPanelComputed,
    ...authComputed,
    ...equipmentComputed,
    ...exerciseComputed,
    ...exerciseDifficultyComputed,
    ...mediaComputed,
    ...muscleGroupComputed,
    isEditable() {
      return (
        this.currentUser.roles.isAdmin ||
        (this.currentUser.roles.isTrainer &&
          this.selectedExercise &&
          this.currentUser.trainer._id === this.selectedExercise.trainer_id)
      );
    },
    hasUnsavedChanges() {
      return (
        this.newUnweightedEquipmentItems ||
        this.hasWeightedEquipmentChanges ||
        this.hasExerciseChanges ||
        this.hasAltExerciseChanges ||
        this.hasStepsChanges ||
        this.hasTipsChanges ||
        this.newCoverPhotoUrl ||
        this.newThumbnailPhotoUrl ||
        this.newVideoUrl
      );
    },
    hasTips() {
      return this.selectedExercise && this.selectedExercise.tips;
    },
    hasSteps() {
      return this.selectedExercise && this.selectedExercise.steps.length > 0;
    },
    hasAltExercise() {
      return (
        this.selectedAltExercise ||
        (this.selectedExercise &&
          this.selectedExercise.alternativeExercise &&
          this.selectedExercise.alternativeExercise.exercise_id)
      );
    },
    hasWeightedEquipment() {
      return (
        this.newWeightedEquipment ||
        (this.selectedExercise &&
          this.selectedExercise.requiredEquipment &&
          this.selectedExercise.requiredEquipment.weighted)
      );
    },
    hasUnweightedEquipment() {
      return (
        (this.newUnweightedEquipment && this.newUnweightedEquipment.length) ||
        (this.selectedExercise &&
          this.selectedExercise.requiredEquipment &&
          this.selectedExercise.requiredEquipment.unweighted.length)
      );
    },
    newUnweightedEquipmentItems() {
      return this.newUnweightedEquipment && this.newUnweightedEquipment.length
        ? this.newUnweightedEquipment
        : null;
    },
    coverPhotoUrl() {
      return this.selectedExercise &&
        this.selectedExercise.mediaAssets &&
        this.selectedExercise.mediaAssets.coverPhoto &&
        this.selectedExercise.mediaAssets.coverPhoto.url
        ? this.selectedExercise.mediaAssets.coverPhoto.url
        : null;
    },
    thumbnailPhotoUrl() {
      return this.selectedExercise &&
        this.selectedExercise.mediaAssets &&
        this.selectedExercise.mediaAssets.thumbnailPhoto &&
        this.selectedExercise.mediaAssets.thumbnailPhoto.url
        ? this.selectedExercise.mediaAssets.thumbnailPhoto.url
        : null;
    },
    coverVideo() {
      return this.tempVideoUrl
        ? this.tempVideoUrl
        : this.selectedExercise &&
          this.selectedExercise.mediaAssets &&
          this.selectedExercise.mediaAssets.coverVideo &&
          this.selectedExercise.mediaAssets.coverVideo.url
        ? this.selectedExercise.mediaAssets.coverVideo.url
        : null;
    },
    coverVideoPhoto() {
      const coverVideoThumbnail = this.selectedExercise?.mediaAssets?.coverVideo
        ?.thumbnail;
      return this.newVideoUrl ? null : coverVideoThumbnail || null;
    },
    selectedMuscleGroups() {
      const selectedMuscleGroups =
        this.selectedExercise && this.selectedExercise.muscleGroups;
      return this.newMuscleGroups || selectedMuscleGroups;
    },
    selectedDifficulty() {
      const selectedDifficulty =
        this.selectedExercise && this.selectedExercise.exerciseDifficulty;
      return this.newDifficulty || selectedDifficulty;
    },
    selectedWeightedEquipment() {
      return (
        this.newWeightedEquipment ||
        (this.selectedExercise &&
          this.selectedExercise.requiredEquipment.weighted)
      );
    },
    hasMuscleGroups() {
      return (
        this.selectedExercise &&
        this.selectedExercise.muscleGroups &&
        this.selectedExercise.muscleGroups.length
      );
    },
    exerciseDifficultyLabel() {
      return this.selectedExercise &&
        this.selectedExercise.exerciseDifficulty &&
        this.selectedExercise.exerciseDifficulty.label
        ? this.selectedExercise.exerciseDifficulty.label
        : 'No Level Set';
    },
    dragOptions() {
      return {
        animation: 200,
        ghostClass: 'dropshadow',
      };
    },
  },
  async created() {
    try {
      if (!this.allExercises.length) {
        this.getAllExerciseDifficulties({
          orgId: this.userOrgId,
          trainerId: '0',
        });
        this.getExerciseShape({ orgId: this.userOrgId, trainerId: '0' });
        await this.getAllExercises({ orgId: this.userOrgId, trainerId: '0' });
      }
      if (!this.allMuscleGroups || !this.allMuscleGroups.length) {
        this.getAllMuscleGroups({ orgId: this.userOrgId, trainerId: '0' });
      }

      if (
        !this.allExerciseDifficulties ||
        !this.allExerciseDifficulties.length
      ) {
        await this.getAllExerciseDifficulties({
          orgId: this.userOrgId,
          trainerId: '0',
        });
      }

      if (!this.allEquipment || !this.allEquipment.length) {
        await this.getAllEquipment({ orgId: this.userOrgId, trainerId: '0' });
      }
    } catch (e) {
      console.log(e);
    }
  },
  beforeMount() {
    this.initExercise = this.compare(cloneDeep(this.exercise));
    this.exerciseObj = this.compare(cloneDeep(this.exercise));
  },
  async mounted() {
    this.init();
    this.setSelectedExercise(this.compare(cloneDeep(this.exercise)));
    if (!this.selectedExercise.tips || !this.selectedExercise.tips.length) {
      this.newTips.push({ id: 1, text: '' });
    }
  },
  destroyed() {
    this.$_clearSelections();
  },
  methods: {
    ...adminPanelMethods,
    ...equipmentMethods,
    ...exerciseMethods,
    ...exerciseDifficultyMethods,
    ...mediaMethods,
    ...muscleGroupMethods,
    ...modalMethods,
    ...workoutMethods,
    ...circuitMethods,
    async showAdminPanel(component, data = null, size = '') {
      await this.setPanelContent(this.getPanelContent(component, data, size));
      this.setShowAdminPanel();
    },
    compare(exercise) {
      exercise.steps.sort(function(a, b) {
        if (a.order < b.order) {
          return -1;
        }
        if (a.order > b.order) {
          return 1;
        }
        return 0;
      });
      return exercise;
    },
    init() {
      if (this.$refs.contentWrapper) {
        this.footerWidth = this.$refs.contentWrapper.offsetWidth;
      }
    },
    showExerciseList() {
      if (this.inPanel) {
        this.setPanelContent({
          ...this.panelContent,
          data: {
            ...this.panelContent.data,
            panelTitle: 'Alternate Exercise',
            componentRight: {
              name: 'ExerciseList',
              showExerciseFilters: true,
              showPanelDetail: true,
            },
          },
        });
      } else {
        this.showAdminPanel(
          'ExerciseList',
          { panelTitle: 'Alternate Exercise' },
          'lg'
        );
      }
    },
    async showPanelDetailContent(component) {
      if (this.inPanel) {
        this.setPanelContent({
          ...this.panelContent,
          data: {
            ...this.panelContent.data,
            componentRight: {
              name: component,
              showPanelDetail: true,
            },
          },
        });
      } else {
        this.showAdminPanel(component, this.exerciseObj);
      }
    },
    addTip() {
      const newTips = this.newTips;
      const lastTip = newTips[newTips.length - 1];

      if (!newTips.length) {
        const tipNum = this.selectedExercise.tips.length + 1;
        newTips.push({ id: tipNum, text: '' });
      } else if (lastTip && lastTip.text !== '') {
        newTips.push({ id: lastTip.id + 1, text: '' });
      }
    },
    editTip(evt, i) {
      this.exerciseObj.tips.splice(i, 1, evt.target.value);
      this.hasExerciseChanges = !isEqual(
        this.selectedExercise,
        this.exerciseObj
      );
    },
    assignMediaUrls() {
      this.exerciseObj.mediaAssets = {
        coverPhoto: {
          url: this.newCoverPhotoUrl || this.coverPhotoUrl,
        },
        coverVideo: {
          url: this.newVideoUrl || this.coverVideo,
          thumbnail: this.coverVideoPhoto || null, // null because the background transcoder will set this when the etsJob finishes
        },
        thumbnailPhoto: {
          url: this.newThumbnailPhotoUrl || this.thumbnailPhotoUrl,
        },
      };
    },
    buildTips() {
      if (this.newTips.length) {
        this.newTips.forEach((tip) => {
          if (tip.text !== '') {
            this.exerciseObj.tips.push(tip.text);
          }
        });
        this.newTips = [];
      }
    },
    addRequiredEquipment() {
      this.exerciseObj.requiredEquipment = {
        weighted:
          this.newWeightedEquipment ||
          this.exerciseObj.requiredEquipment.weighted,
        unweighted: this.newUnweightedEquipment.length
          ? this.newUnweightedEquipment
          : this.exerciseObj.requiredEquipment.unweighted,
      };
    },
    applyAltExercise() {
      if (
        this.selectedAltExercise &&
        this.selectedAltExercise.label &&
        this.selectedAltExercise._id
      ) {
        this.exerciseObj.alternativeExercise = {
          exercise_id:
            this.selectedAltExercise._id ||
            this.exerciseObj.alternativeExercise.exercise_id,
          label:
            this.selectedAltExercise.label ||
            this.exerciseObj.alternativeExercise.label,
        };
      }
    },
    async buildExercisePayload() {
      await this.assignMediaUrls();
      await this.buildTips();
      await this.addRequiredEquipment();
      await this.applyAltExercise();
      return this.exerciseObj;
    },
    onSelectMuscleGroup(value) {
      if (this.selectedMuscleGroups && this.selectedMuscleGroups.length) {
        const matchesMuscleGroup = this.selectedMuscleGroups.find((group) => {
          if (group._id === value._id || group.muscleGroup_id === value._id) {
            return group;
          }
        });

        if (matchesMuscleGroup) {
          this.setRemoveExerciseMuscleGroup(value._id);
        } else {
          this.exerciseObj.muscleGroups.push({
            muscleGroup_id: value._id,
            label: value.label,
          });
          this.setSelectedExercise(this.exerciseObj);
        }
      } else {
        this.exerciseObj.muscleGroups.push({
          muscleGroup_id: value._id,
          label: value.label,
        });
        this.setSelectedExercise(this.exerciseObj);
      }
    },
    isSelectedMuscleGroup(id) {
      if (this.selectedMuscleGroups && this.selectedMuscleGroups.length) {
        const matchesMuscleGroup = this.selectedMuscleGroups.find((group) => {
          if (group._id === id || group.muscleGroup_id === id) {
            return group;
          }
        });

        if (matchesMuscleGroup) {
          return true;
        }
      }
    },
    onSelectDifficulty(value) {
      this.exerciseObj.exerciseDifficulty = {
        exerciseDifficulty_id: value._id,
        label: value.label,
      };
      this.setSelectedExercise(this.exerciseObj);
      this.newDifficulty = value;
    },
    onSelectEquipment(value) {
      if (value) {
        if (value.isWeighted) {
          this.newWeightedEquipment = {
            _id: value._id,
            label: value.label,
          };
        } else {
          if (this.newUnweightedEquipment.length === 0) {
            this.newUnweightedEquipment.push(
              ...this.exerciseObj.requiredEquipment.unweighted
            );
          }

          this.newUnweightedEquipment.push({
            _id: value._id,
            label: value.label,
          });
        }
      }
    },
    equipmentSelectable(equipment) {
      if (this.newUnweightedEquipment.length) {
        return !this.newUnweightedEquipment.some(
          (e) => e._id === equipment._id
        );
      } else {
        return !this.exerciseObj.requiredEquipment.unweighted.some(
          (e) => e._id === equipment._id
        );
      }
    },

    onUpdateExerciseProps(exercise) {
      this.setSelectedExercise(exercise);
    },
    onRemoveWeightedEquip() {
      this.newWeightedEquipment
        ? (this.newWeightedEquipment = null)
        : this.setRemoveWeightedEquip();
    },
    onRemoveUnweightedEquip(equip) {
      if (this.newUnweightedEquipment.length) {
        const i = this.newUnweightedEquipment.findIndex(
          (item) => item._id === equip._id
        );
        this.newUnweightedEquipment.splice(i, 1);
      } else {
        this.setRemoveUnweightedEquip(equip);
      }
    },
    resetFormOnSubmit() {
      this.initExercise = cloneDeep(this.selectedExercise);
      this.isSaving = false;
      this.newUnweightedEquipment = [];
      this.hasWeightedEquipmentChanges = false;
      this.hasExerciseChanges = false;
      this.hasAltExerciseChanges = false;
      this.hasStepsChanges = false;
      this.hasTipsChanges = false;
      this.newVideoUrl = false;
      this.newThumbnailPhotoUrl = false;
      this.newCoverPhotoUrl = false;
      this.setHasUnsavedExerciseChanges(false);
    },
    handleSaveExercise() {
      this.inPanel ? this.onCreate() : this.onUpdate();
    },
    async onUpdate() {
      this.isSaving = true;
      const payload = await this.buildExercisePayload();
      this.setSelectedExercise(this.exerciseObj);
      this.tempVideoUrl = null;
      this.updateExercise({
        trainerId: this.selectedExercise.trainer_id,
        exerciseId: this.selectedExercise._id,
        exerciseObj: { ...this.selectedExercise, ...payload },
      });

      if (
        this.currentUser &&
        this.currentUser.roles &&
        this.currentUser.roles.isTrainer
      ) {
        const exercise = this.selectedExercise._id;
        const trainer = this.trainerEmail;
        this.$_pushCleverTapEvent('Edit Exercise', { exercise, trainer });
      }

      this.resetFormOnSubmit();
      this.lastSaved = `${moment().format('MMMM Do')} at ${moment().format(
        'hh:mm a'
      )}`;
      if (!this.selectedExercise.tips || !this.selectedExercise.tips.length) {
        this.newTips.push({ id: 1, text: '' });
      }

      this.getAllWorkouts({
        orgId: this.userOrgId,
        trainerId: this.trainerId ? this.trainerId : '0',
      });

      this.getAllCircuits({
        orgId: this.userOrgId,
        trainerId: this.trainerId ? this.trainerId : '0',
      });
    },
    async onCreate() {
      this.isSaving = true;
      await this.createExercise({
        exerciseObj: {
          ...this.exerciseObj,
          tips: this.exerciseObj.tips || [],
          steps: this.exerciseObj.steps || [],
          alternativeExercise: this.exerciseObj || null,
          mediaAssets: {
            coverPhoto: {
              url: this.newCoverPhotoUrl || null,
            },
            coverVideo: {
              url: this.newVideoUrl || null,
              thumbnail: null, // null because the background transcoder will set this when the etsJob finishes
            },
            thumbnailPhoto: {
              url: this.newThumbnailPhotoUrl || null,
            },
          },
          requiredEquipment: {
            weighted: this.newWeightedEquipment || null,
            unweighted: this.newUnweightedEquipment || [],
          },
          trainer_id: this.trainerId || '0',
          org_id: this.userOrgId,
        },
        trainerId: this.trainerId || '0',
      })
        .then((res) => {
          if (res.success) {
            this.resetFormOnSubmit();
            this.setHideAdminPanel();

            if (
              this.currentUser &&
              this.currentUser.roles &&
              this.currentUser.roles.isTrainer
            ) {
              const exercise = res.exercise._id;
              const trainer = this.trainerEmail;
              this.$_pushCleverTapEvent('Create Exercise', {
                exercise,
                trainer,
              });
            }
          }
        })
        .catch((e) => console.log(e));
    },
    cancelChanges() {
      this.tempVideoUrl = null;
      this.$refs.coverPhotoUploader.photoPreview = '';
      this.$refs.thumbnailPhotoUploader.photoPreview = '';
      this.$refs.videoUploader.videoSrc = null;
      this.$refs.videoUploader.videoType = null;
      this.clearMedia();
      this.exerciseObj = cloneDeep(this.initExercise);
      this.setSelectedExercise(this.exerciseObj);
      this.setSelectedAltExercise(null);
      this.newDifficulty = null;
      this.newMuscleGroups = null;
      this.newUnweightedEquipment = [];
      this.newWeightedEquipment = null;

      if (
        !this.selectedExercise.tips ||
        (!this.selectedExercise.tips.length && !this.newTips.length)
      ) {
        this.newTips.push({ id: 1, text: '' });
      }
      this.hasExerciseChanges = false;
      this.setHasUnsavedExerciseChanges(false);
    },
    confirmDelete() {
      this.setSelectedItem({
        ...this.selectedExercise,
        context: 'Exercise',
      });
      this.$bvModal.show('confirmation-modal');
    },
  },
  watch: {
    newTips: {
      deep: true,
      handler(newVal, oldVal) {
        this.hasTipsChanges = !isEqual(newVal, [{ id: 1, text: '' }]);
      },
    },
    selectedExercise: {
      deep: true,
      handler(newVal, oldVal) {
        if (oldVal !== null) {
          this.exerciseObj = cloneDeep(this.selectedExercise);
          this.hasExerciseChanges = !isEqual(
            this.selectedExercise,
            this.initExercise
          );
        }
        // Fire vuex method to prevent navigation and lost changes
        this.setHasUnsavedExerciseChanges(this.hasExerciseChanges);
      },
    },
    selectedAltExercise(newVal, oldVal) {
      if (newVal === null) {
        this.hasAltExerciseChanges = false;
      } else {
        this.hasAltExerciseChanges = true;
      }
    },
    newWeightedEquipment(newVal, oldVal) {
      if (newVal === null) {
        this.hasWeightedEquipmentChanges = false;
      } else {
        this.hasWeightedEquipmentChanges = true;
      }
    },
    isAdminPanelActive(newVal, oldVal) {
      if (!newVal && oldVal) {
        this.newCoverPhotoUrl = null;
        this.newThumbnailPhotoUrl = null;
        this.newVideoUrl = null;
        this.newVideoType = null;
        this.setPanelContent(null);
      }
    },
    signedPostData(newVal, oldVal) {
      if (newVal && newVal.length) {
        const coverPhoto = newVal.find(
          (upload) =>
            upload.requestType === 'exercises' && upload.type === 'cover'
        );
        const thumbnailPhoto = newVal.find(
          (upload) =>
            upload.requestType === 'exercises' && upload.type === 'thumbnail'
        );
        const video = newVal.find(
          (upload) =>
            upload.requestType === 'exercises' && upload.type === 'video'
        );
        this.newCoverPhotoUrl =
          this.newCoverPhotoUrl || coverPhoto?.publicUrl || null;
        this.newThumbnailPhotoUrl =
          this.newThumbnailPhotoUrl || thumbnailPhoto?.publicUrl || null;
        this.newVideoUrl = this.newVideoUrl || video?.publicUrl || null;

        if (video) {
          this.newVideoType = video?.file?.type;
          this.mediaKey++;
        }
      }
    },
  },
};
</script>

<template>
  <div :class="['exercise-detail', { 'in-panel': inPanel }]">
    <HeroExercise
      v-if="selectedExercise"
      :initExercise="initExercise"
      :currentExercise="selectedExercise"
      :exercise="exercise"
      :isEditable="isEditable"
      :inPanel="inPanel"
      @update-exercise-props="onUpdateExerciseProps"
    />

    <SectionWrapper bg-color="white">
      <div class="row specifications">
        <div class="col-7 first">
          <div class="row">
            <div class="col">
              <label for="muscle_group">Muscle Group</label>
              <v-select
                multiple
                v-if="isEditable || inPanel"
                class="tdm-multi-select"
                :closeOnSelect="false"
                :options="allMuscleGroups"
                :searchable="false"
                :clearable="false"
                :value="selectedMuscleGroups"
              >
                <template v-slot:selected-option-container="{ option }">
                  <div
                    v-if="
                      selectedMuscleGroups && selectedMuscleGroups.length > 1
                    "
                  >
                    {{ selectedMuscleGroups.length }} Selected
                  </div>
                  <div v-else>{{ option.label }}</div>
                </template>
                <template v-slot:option="{ _id, label }">
                  <div
                    class="option-label-wrapper"
                    @click="onSelectMuscleGroup({ _id, label })"
                  >
                    <div>{{ label }}</div>
                    <BaseIcon
                      class="icon-check"
                      name="check"
                      v-if="isSelectedMuscleGroup(_id)"
                    />
                  </div>
                </template>
              </v-select>
              <p class="read-only-muscle-groups" v-if="!isEditable">
                <template v-if="hasMuscleGroups && !inPanel">
                  <span
                    v-for="(group, i) in selectedExercise.muscleGroups"
                    :key="i"
                  >
                    {{ group.label
                    }}<span v-if="i + 1 < selectedExercise.muscleGroups.length"
                      >,</span
                    >
                  </span>
                </template>
                <span v-if="!hasMuscleGroups && !inPanel"
                  >No Muscle Groups Set</span
                >
              </p>
            </div>

            <div class="col">
              <label for="level" v-if="isEditable || inPanel">Level</label>
              <v-select
                v-if="isEditable || inPanel"
                :options="allExerciseDifficulties"
                :searchable="false"
                :clearable="false"
                :value="selectedDifficulty"
                @input="onSelectDifficulty"
              />
              <p v-else>{{ exerciseDifficultyLabel }}</p>
            </div>
          </div>

          <div class="row">
            <div class="col">
              <label for="alt_exercise">Alternative Exercise</label>
              <span class="helper-text" v-if="isEditable || inPanel">
                For users that need a slightly modified version of the exercise,
                what should they reference instead?
              </span>

              <div class="input-btn-icon-wrapper icon-right">
                <svg width="22" height="23" viewBox="0 0 22 23" fill="none" xmlns="http://www.w3.org/2000/svg" v-if="currentUser.isEditable" v-bind:svg-inline="''" @click.stop="showExerciseList" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M5.666 17.263a3.103 3.103 0 010-4.384l2.828-2.828-1.343-1.344-2.829 2.829a5.002 5.002 0 000 7.07 5.002 5.002 0 007.071 0l2.829-2.828-1.344-1.343-2.828 2.828a3.103 3.103 0 01-4.384 0zm3.606-2.192l5.657-5.657L13.514 8l-5.656 5.657 1.414 1.414zm-.707-7.778l2.828-2.829a5.002 5.002 0 017.071 0 5.002 5.002 0 010 7.072l-2.828 2.828-1.344-1.344 2.829-2.828a3.103 3.103 0 000-4.384 3.103 3.103 0 00-4.384 0L9.908 8.636 8.565 7.293z" fill="#000"/></svg>
                <BaseButton
                  type="button"
                  v-if="isEditable || inPanel"
                  class="btn-input tan"
                  @click="showExerciseList"
                >
                  <span v-if="hasAltExercise">{{
                    (selectedAltExercise && selectedAltExercise.label) ||
                      selectedExercise.alternativeExercise.label
                  }}</span>
                </BaseButton>
                <p v-else>
                  <span v-if="hasAltExercise">
                    {{
                      (selectedAltExercise && selectedAltExercise.label) ||
                        selectedExercise.alternativeExercise.label
                    }}
                  </span>
                  <span v-else>No Linked Exercises</span></p
                >
              </div>
            </div>
          </div>

          <div class="row" v-if="isEditable || inPanel">
            <div class="col">
              <label for="tips">Tips (Optional)</label>
              <template v-if="exerciseObj">
                <div v-for="(tip, i) in exerciseObj.tips" :key="i">
                  <BaseInputText :value="tip" @blur="editTip($event, i)" />
                </div>
              </template>

              <div v-for="input in newTips" :key="input.id">
                <BaseInputText v-model="input.text" />
              </div>
              <BaseButton
                type="button"
                class="btn-secondary btn-round"
                @click="addTip"
              >
                Add another tip
              </BaseButton>
            </div>
          </div>
        </div>
        <div class="col">
          <label for="equip_search" v-if="isEditable || inPanel"
            >Equipment Required</label
          >
          <v-select
            :options="allEquipment"
            :selectable="(equipment) => equipmentSelectable(equipment)"
            placeholder="Start typing to find equipment..."
            @input="onSelectEquipment"
            v-if="isEditable || inPanel"
          >
            <template v-slot:no-options="{ search, searching }">
              <template v-if="searching">
                No results found.
                <span
                  class="link blue"
                  @click="showAdminPanel('EquipmentForm')"
                >
                  Create a new piece of equipment.
                </span>
              </template>
            </template>
          </v-select>

          <div class="tags-container">
            <label for="equip_tags_weighted" v-if="hasWeightedEquipment"
              >Weighted (Limit 1)</label
            >
            <BaseCheckboxTags
              class="checkbox-tags"
              name="equip_tags_weighted"
              v-if="(hasWeightedEquipment && isEditable) || inPanel"
              :item="
                newWeightedEquipment || exerciseObj.requiredEquipment.weighted
              "
              @select-tag="onRemoveWeightedEquip"
            />
            <p
              v-else-if="
                hasWeightedEquipment && exerciseObj.requiredEquipment.weighted
              "
              >{{ exerciseObj.requiredEquipment.weighted.label }}</p
            >
          </div>

          <div class="tags-container">
            <label for="equip_tags_unweighted" v-if="hasUnweightedEquipment"
              >Unweighted (No Limit)</label
            >
            <BaseCheckboxTags
              class="checkbox-tags"
              name="equip_tags_unweighted"
              v-if="(hasUnweightedEquipment && isEditable) || inPanel"
              :items="
                newUnweightedEquipmentItems ||
                  exerciseObj.requiredEquipment.unweighted
              "
              @select-tag="onRemoveUnweightedEquip"
            />
            <p v-else-if="hasUnweightedEquipment">
              <span
                v-for="(equipment, index) in exerciseObj.requiredEquipment
                  .unweighted"
                :key="index"
              >
                {{ equipment.label
                }}<span
                  v-if="
                    index !==
                      Object.keys(exerciseObj.requiredEquipment.unweighted)
                        .length -
                        1
                  "
                  >,
                </span>
              </span>
            </p>
          </div>
        </div>
      </div>
    </SectionWrapper>

    <SectionWrapper title="Media Uploads">
      <div class="row uploaders">
        <div class="col with-divider">
          <VideoUploader
            requestType="exercises"
            type="video"
            ref="videoUploader"
            title="Workout Video"
            helper-text=" "
            :key="mediaKey"
            :hideButton="!isEditable && !inPanel"
            :existingPhoto="coverVideoPhoto"
            :videoSrc="coverVideo"
            :newVideoType="newVideoType"
            @update-temp-video-url="tempVideoUrl = $event"
          />
        </div>
        <div class="col with-divider">
          <PhotoUploader
            :isExpandable="true"
            :hideButton="!isEditable && !inPanel"
            requestType="exercises"
            ref="coverPhotoUploader"
            :existingPhoto="coverPhotoUrl"
            type="cover"
            title="Cover Photo"
            helper-text=" "
          />
        </div>
        <div class="col">
          <PhotoUploader
            :isExpandable="true"
            :hideButton="!isEditable && !inPanel"
            requestType="exercises"
            ref="thumbnailPhotoUploader"
            :existingPhoto="thumbnailPhotoUrl"
            type="thumbnail"
            title="Thumbnail Photo"
            helper-text=" "
          />
        </div>
      </div>
    </SectionWrapper>

    <SectionWrapper class="section-steps" bg-color="white" title="Steps">
      <draggable
        v-model="exerciseObj.steps"
        class="draggable-list"
        v-bind="dragOptions"
        v-if="(hasSteps && isEditable) || inPanel"
        @start="drag = true"
        @end="drag = false"
      >
        <StepListItem
          v-for="(step, i) in exerciseObj.steps"
          :key="step.order"
          :step="step"
          :stepIndex="i"
        />
      </draggable>

      <ol v-if="hasSteps && !isEditable">
        <StepListItem
          :hideControls="!isEditable && !inPanel"
          v-for="(step, i) in exerciseObj.steps"
          :key="step.order"
          :step="step"
          :stepIndex="i"
        />
      </ol>

      <p v-if="!hasSteps">No steps available</p>

      <BaseButton
        v-if="isEditable || inPanel"
        class="btn-secondary btn-round"
        type="button"
        @click="showPanelDetailContent('ExerciseStepForm')"
      >
        <BaseIcon name="plus" /> Add Step
      </BaseButton>
    </SectionWrapper>

    <ActionFooter
      v-if="isEditable || inPanel"
      color="black"
      :style="`width: ${footerWidth}px;`"
    >
      <span
        v-if="!hasUnsavedChanges && lastSaved"
        class="saved-status"
        slot="actionState"
      >
        Last Saved: {{ lastSaved }}
      </span>
      <span
        v-if="!hasUnsavedChanges && !lastSaved"
        class="saved-status"
        slot="actionState"
        >No changes</span
      >
      <span v-if="hasUnsavedChanges" slot="secondaryAction">
        Not saved
        <span class="link-deselect" @click="cancelChanges">
          Cancel
        </span>
      </span>
      <BaseButton
        v-if="!inPanel"
        type="button"
        class="btn-primary btn-clear"
        @click="confirmDelete"
      >
        <svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-trash'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.5 1H14v2H0V1h3.5l1-1h5l1 1zM3 18c-1.1 0-2-.9-2-2V4h12v12c0 1.1-.9 2-2 2H3z" fill="#000"/></svg>
        Delete Exercise
      </BaseButton>
      <span class="divider-vertical" v-if="!inPanel">&nbsp;</span>
      <BaseButton
        @click="handleSaveExercise"
        type="button"
        class="btn-primary btn-clear"
        :disabled="!hasUnsavedChanges"
        :style="{ opacity: !hasUnsavedChanges || isSaving ? 0.5 : 1 }"
      >
        <BaseIcon v-if="isSaving" name="sync" spin />
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" v-if="!isSaving" v-bind:class="'icon-save'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M13.222 0H1.89C.84 0 0 .85 0 1.889V15.11C0 16.15.84 17 1.889 17H15.11c1.04 0 1.89-.85 1.89-1.889V3.778L13.222 0zM8.5 15.111a2.83 2.83 0 01-2.833-2.833A2.83 2.83 0 018.5 9.444a2.83 2.83 0 012.833 2.834A2.83 2.83 0 018.5 15.11zM1.889 5.667h9.444V1.889H1.89v3.778z" fill="#fff"/></svg>
        Save Exercise
      </BaseButton>
    </ActionFooter>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.exercise-detail {
  .col,
  div[class^='col-'] {
    &.with-divider {
      border-right: 1px solid $color-border-light-gray;
    }
  }

  .specifications {
    div[class^='col-'] {
      &.first {
        padding-right: 16.9rem;
      }
    }

    .read-only-muscle-groups {
      margin-bottom: 2.2rem;
    }

    .tags-container {
      width: 70%;
    }
  }

  .uploaders {
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(auto-fill, minmax(30rem, 1fr));
    .col {
      &:not(:first-of-type) {
        padding-left: 3rem;
      }

      &:not(:last-of-type) {
        padding-right: 3rem;
      }
    }
  }

  .draggable-list {
    margin-bottom: 2rem;
  }

  .dropshadow {
    background: $color-light-gray;
    opacity: 0.4;
  }

  .flip-list-move {
    transition: transform 0.5s;
  }

  .section-steps,
  .section-default-values {
    padding-bottom: 22rem;
  }

  .default-exercise-values {
    .header {
      display: flex;
      justify-content: space-around;
      padding: 0 0 0.5rem;
    }
  }

  .action-footer-wrapper {
    padding: 0 4rem;
  }

  &.in-panel {
    .hero {
      background-color: $color-off-white;
    }

    .hero,
    .section-wrapper {
      padding-left: 3rem;
      padding-right: 3rem;
    }

    .action-footer-wrapper {
      padding: 0 3rem;
      width: 65%;
    }
  }
  .vs__dropdown-option--disabled {
    opacity: 0.8;
  }
}
</style>
